import { getExtension } from "@/components/AddData/fileName";

export const uploadDataTypes = {
	pointcloud: "pointcloud",
	design: "design",
	drawing2dImage: "drawing2dImage",
	lineworks: "lineworks",
	linearfile: "linearfile",
} as const;

export type UploadDataType =
	(typeof uploadDataTypes)[keyof typeof uploadDataTypes];

export const uploadSources = {
	local: "local",
	groupware: "groupware",
} as const;

export type UploadSource = (typeof uploadSources)[keyof typeof uploadSources];

/** LAS-family point cloud extensions: imported as is, without an axis layout. */
export const LAS_POINT_CLOUD_EXTENSIONS = ["las", "laz"] as const;

// Groupware の単一ファイル経路（FileSelectionDialog -> addTerrainData）で拡張子だけから種別が決まる形式。
// tiff の組はピッカーから resolveGroupwareAccept を通る別経路なので、この集合には含めない（GI-34）
// 型注釈は readonly string[]（as const にすると includes 判定に as never が必要になる）
const GROUPWARE_ALLOWED_EXTENSIONS: readonly string[] = [
	"las",
	"laz",
	"txt",
	"csv",
	"xml",
	"dxf",
];

/**
 * DTM / オルソの組としてのみ扱う形式。resolveUploadTypeByExtension は経路によらず null を返す。
 * fileSelectionResolver / groupwareSelectionResolver の tiff 判定は isTiffFileName を通してこの 1 箇所を参照する
 */
const TIFF_EXTENSIONS: readonly string[] = ["tif", "tiff"];

/** ファイル名の拡張子が tif / tiff か（大文字小文字は getExtension が吸収する。GI-1） */
export const isTiffFileName = (name: string): boolean => {
	const extension = getExtension(name);
	return extension !== null && TIFF_EXTENSIONS.includes(extension);
};

// tif / tiff は accept には出すが resolveUploadTypeByExtension は null を返す
// （DTM / オルソのペア限定で、種別選択ダイアログ経由の専用フローに進む）。
// 連結順は現行と同一（accept 文字列が 1 文字も変わらない。GI-34）
const LOCAL_ONLY_ALLOWED_EXTENSIONS: readonly string[] = [
	"sim",
	"jpeg",
	"jpg",
	"png",
	...TIFF_EXTENSIONS,
];

const toAcceptValue = (extensions: ReadonlyArray<string>): string =>
	extensions.map((extension) => `.${extension}`).join(",");

/**
 * Whether the file is a LAS / LAZ point cloud (extension, case-insensitive).
 *
 * @param fileName - File name to check; null / undefined -> false
 */
export const isLasPointCloudFile = (
	fileName: string | null | undefined,
): boolean => {
	const extension = fileName ? getExtension(fileName)?.toLowerCase() : null;
	if (!extension) {
		return false;
	}
	const lasExtensions: ReadonlyArray<string> = LAS_POINT_CLOUD_EXTENSIONS;
	return lasExtensions.includes(extension);
};

/**
 * Resolves the upload data type based on file extension and source.
 *
 * @param fileName - The name of the file to resolve
 * @param source - The source of the upload (local or groupware). Defaults to "local".
 * @returns The resolved upload data type, or null if the extension is not supported or invalid
 *
 * The extension comes from the shared `getExtension` helper, so the comparison is case-insensitive
 * and files without an extension (including dotfiles) resolve to null.
 *
 * Conversion rules:
 * - las, laz, csv, txt -> pointcloud
 * - xml -> design
 * - dxf -> design (with further selection available in addTerrainData)
 * - jpeg, jpg, png -> drawing2dImage
 * - sim (local only) -> linearfile
 * - tif, tiff -> null (handled by the DTM/ortho pair flow, not by this resolver)
 * - Groupware allows only: las, laz, txt, csv, xml, dxf (sim and tiff are excluded)
 * - Other/no extension -> null
 */
export const resolveUploadTypeByExtension = (
	fileName: string,
	source: UploadSource = uploadSources.local,
): UploadDataType | null => {
	const extension = getExtension(fileName);
	if (!extension) {
		return null;
	}

	// Groupware source: only allow specific extensions
	if (source === uploadSources.groupware) {
		if (!GROUPWARE_ALLOWED_EXTENSIONS.includes(extension)) {
			return null;
		}
	}

	if (extension === "xml") {
		return uploadDataTypes.design;
	}

	if (isLasPointCloudFile(fileName) || ["csv", "txt"].includes(extension)) {
		return uploadDataTypes.pointcloud;
	}

	if (["jpeg", "jpg", "png"].includes(extension)) {
		return uploadDataTypes.drawing2dImage;
	}

	if (extension === "dxf") {
		return uploadDataTypes.design;
	}

	if (extension === "sim") {
		return uploadDataTypes.linearfile;
	}

	return null;
};
/**
 * Returns a comma-separated string of allowed file extensions for the given upload source.
 *
 * @param source - The source of the upload (local or groupware)
 * @returns Comma-separated file extensions allowed for the source (e.g., ".las,.txt,.csv,.xml,.dxf")
 */
export const getAllowedFileTypesString = (source: UploadSource): string => {
	if (source === uploadSources.groupware) {
		return toAcceptValue(GROUPWARE_ALLOWED_EXTENSIONS);
	}
	// local source allows all extensions
	return toAcceptValue([
		...GROUPWARE_ALLOWED_EXTENSIONS,
		...LOCAL_ONLY_ALLOWED_EXTENSIONS,
	]);
};
